import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyledCopyIcon } from 'components/v2/epd-compiler-inputs/common-icons';
import Tooltip from 'components/controls/Tooltip';
import { ReactComponent as CheckIconComponent } from 'images/v2/icons/svg/fill/svg/check.svg';
import styled from 'styled-components';
import { MAX_FIELD_LENGTH } from '../constants';

interface Props {
  email: string | null | undefined;
}

const COPY_FEEDBACK_DELAY_MS = 4000;

const EmailCell: React.FC<Props> = ({ email }) => {
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!email) return;
      navigator.clipboard.writeText(email).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPY_FEEDBACK_DELAY_MS);
      });
    },
    [email]
  );

  useEffect(() => {
    if (!copied) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) setCopied(false);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [copied]);

  if (!email) {
    return null;
  }

  const isTruncated = email.length > MAX_FIELD_LENGTH;
  const truncatedEmail = email.substring(0, MAX_FIELD_LENGTH).replace(/\.$/, '');
  const displayEmail = isTruncated ? truncatedEmail + '...' : email;

  return (
    <Container ref={containerRef}>
      <EmailWrapper>
        <EmailLink href={`mailto:${email}`}>{displayEmail}</EmailLink>
        {isTruncated && <Tooltip position={'mouse'}>{email}</Tooltip>}
      </EmailWrapper>
      <CopyWrapper>
        {copied ? <StyledCheckIcon /> : <StyledCopyIcon onClick={handleCopy} />}
        <Tooltip position={'bottom'} forceVisible={copied}>{copied ? 'Copied' : 'Copy email'}</Tooltip>
      </CopyWrapper>
    </Container>
  );
};

const Container = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EmailWrapper = styled.span`
  position: relative;
`;

const CopyWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  position: relative;
`;

const StyledCheckIcon = styled(CheckIconComponent)`
  path {
    stroke: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const EmailLink = styled.a`
  color: ${(props) => props.theme.colors.primaryOrange};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default EmailCell;
